import { AuditCategory, DoctrineStatus, Verdict } from './engine/types';

// User-facing verdict labels reframe the tool from judge → investigation-prompter (CA-9): a flag that
// turns out fine isn't "wrong", it's an investigation that came back clean. The internal enum
// (Good/Concerning/Alarming) is unchanged — only these display labels change.
export const VerdictLabel: Record<Verdict, string> = {
  Good: 'Clear',
  Concerning: 'Recommend investigation',
  Alarming: 'Investigation required'
};

// Short forms for space-tight spots (pills, chips); pair with the full label via a title tooltip.
export const VerdictAbbrev: Record<Verdict, string> = {
  Good: 'Clear',
  Concerning: 'Recommend inv.',
  Alarming: 'Investigate'
};

export const VerdictClass: Record<Verdict, string> = {
  Good: 'good',
  Concerning: 'concerning',
  Alarming: 'alarming'
};

export const CATEGORY_ORDER: AuditCategory[] = [
  'Relationship / Client Status',
  'Strategic',
  'Tactical',
  'Technical'
];

export const AuditCategoryShortLabel: Record<AuditCategory, string> = {
  'Relationship / Client Status': 'Relationship',
  Strategic: 'Strategic',
  Tactical: 'Tactical',
  Technical: 'Technical'
};

// Per-category framing. The tool's ~128 questions span four different KINDS of audit, and reviewers
// (Shane Sabran/SVP, Liat, Luke, Corey — 2026-08) converged that they shouldn't read as one flat
// list. Shane's frame: a tactical gap, a relationship gap, and a strategic gap are different problems,
// for different owners, on different cadences — and unaddressed relationship/strategic gaps are a
// leading indicator of churn, while a clean account shifting toward brand is a growth signal. This map
// surfaces that purpose on each category tab. NOTE: copy is a v1 draft pending Shane/Harry review.
export interface CategoryFrame {
  gapType: string; // Shane's gap-taxonomy label for this category
  purpose: string; // what a gap in this category actually means
  owner: string; // who this audit is for / who acts on a finding
  cadence: string; // how often to run or revisit it
  action: string; // what to do with a finding here
}

export const CATEGORY_FRAME: Record<AuditCategory, CategoryFrame> = {
  Technical: {
    gapType: 'Execution / compliance gap',
    purpose:
      'Is the campaign set up and running correctly — tagging, ad server, hygiene, delivery?',
    owner: 'AdOps / Media Investment',
    cadence: 'Frequent — most of these are automatable; check monthly or on any setup change.',
    action: 'Fix the setup. These are concrete, ownable to-dos.'
  },
  Tactical: {
    gapType: 'Tactical gap',
    purpose:
      'Are we using the right levers — targeting, 1P data, bidding, structure — to hit the plan?',
    owner: 'Channel / DSP strategist (Client Delivery)',
    cadence: 'Quarterly, or whenever the plan changes.',
    action: 'Build the fix — adjust the tactic or the plan.'
  },
  Strategic: {
    gapType: 'Strategic gap',
    purpose:
      'Are we measuring and growing the account correctly — measurement validity (MMM / MTA / incrementality), full-funnel and upper-funnel readiness?',
    owner: 'Strategy / analytics lead',
    cadence: 'Quarterly to semi-annual — this is critical-thinking, not a checklist.',
    action:
      'Validate or pitch — e.g. stand up an incrementality study, or pitch brand / full-funnel work. A clean account starting to ask about awareness or incrementality is a growth signal.'
  },
  'Relationship / Client Status': {
    gapType: 'Relationship gap',
    purpose:
      'Is the client engaged, is leadership bought in, and are we set up to keep and grow the account?',
    owner: 'Account lead / leadership',
    cadence: 'Ongoing — revisit each quarter.',
    action:
      'Escalate to account leadership. Unaddressed gaps here are a leading indicator of churn risk.'
  }
};

// Audit roles — who audits which rows (Luke Stadel, alpha 2026-08: "cluster those separately so the
// right person is auditing the right things"). The role is DERIVED FROM CATEGORY (Harry 2026-09-01):
// every row in a category shares the same owner(s), so there is no per-row tag — the mapping below is
// the whole model. Used for the category-header role chips and the Overview role filter.
export const AuditRole = {
  MEDIA_INVESTMENT: 'media-investment',
  TVAD_LEAD: 'tvad-lead',
  CLIENT_PARTNER: 'client-partner'
} as const;
export type AuditRoleType = (typeof AuditRole)[keyof typeof AuditRole];

export const AuditRoleLabel: Record<AuditRoleType, string> = {
  [AuditRole.MEDIA_INVESTMENT]: 'Media Investment',
  [AuditRole.TVAD_LEAD]: 'TVAD Lead',
  [AuditRole.CLIENT_PARTNER]: 'Client Partner'
};

export const AUDIT_ROLE_ORDER: AuditRoleType[] = [
  AuditRole.MEDIA_INVESTMENT,
  AuditRole.TVAD_LEAD,
  AuditRole.CLIENT_PARTNER
];

export const ROLES_BY_CATEGORY: Record<AuditCategory, AuditRoleType[]> = {
  Technical: [AuditRole.MEDIA_INVESTMENT],
  Tactical: [AuditRole.MEDIA_INVESTMENT, AuditRole.TVAD_LEAD],
  Strategic: [AuditRole.TVAD_LEAD, AuditRole.CLIENT_PARTNER],
  'Relationship / Client Status': [AuditRole.CLIENT_PARTNER]
};

// Funnel-coverage rollup for the Overview (Lindsey, 2026-08: "targeting all parts of the funnel").
// A curated grouping of the existing rows that together describe funnel balance — surfaced in one
// place rather than as a new (fuzzy) composite verdict. Ids are the stable catalog ids.
export const FUNNEL_SIGNAL_IDS: { id: number; label: string }[] = [
  { id: 27, label: 'Retargeting reliance' },
  { id: 24, label: 'DR-inventory concentration' },
  { id: 22, label: 'Secondary audiences' },
  { id: 97, label: 'Optimization-goal alignment' },
  { id: 76, label: 'Sequential messaging' }
];

export const OVERVIEW_KEY = 'overview';

export const GUIDANCE_KEY = 'guidance';

export const ACTIONS_KEY = 'actions';

export const HOWTO_KEY = 'how-to-use';

// The four category tabs are consolidated behind one "Audit questions" tab (Harry 2026-09-09). Eight
// tabs, four of which were near-identical long lists, was the single biggest driver of the
// "cumbersome and daunting" feedback; inside this tab the categories become collapsed segments so the
// detail is expandable rather than always present.
export const QUESTIONS_KEY = 'audit-questions';

// Bug / data-inaccuracy intake form, surfaced in the up-front disclaimer (Harry 2026-09-09). This
// is the reporting path for the whole tool — keep it in the disclaimer everyone has to acknowledge.
export const AUDIT_INTAKE_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSfTkpk87aTNVqEyyfjWkzfCLK_Ulk12sCl95rWvUaNvEDhcyA/viewform?usp=dialog';

export const VERDICT_OPTIONS: Verdict[] = ['Good', 'Concerning', 'Alarming'];

export const DoctrineLabel: Record<DoctrineStatus, string> = {
  PINNED: 'Pinned',
  THIN: 'Unpinned',
  GAP: 'Gap'
};

export const DoctrineClass: Record<DoctrineStatus, string> = {
  PINNED: 'pinned',
  THIN: 'thin',
  GAP: 'gap'
};
